/* Fresh Coast — Products mega menu controller.
 *
 * The PRODUCTS item opts out of Horizon's hover machinery (no on:pointerenter
 * bindings) and is driven here instead. The panel is a DOM descendant of the
 * trigger's <li>, so moving the pointer down into the panel never fires the
 * item's mouseleave; a short close delay only bridges the header→panel gap.
 *
 * Trigger affordance is context-aware: on hover-capable pointers the panel
 * opens on hover/focus and a click (or Enter) follows the link to the Products
 * page. On touch (no hover) there's nowhere to hover, so the first tap opens
 * the panel and the cards do the navigating. Space always toggles; Escape and
 * outside-click close.
 */
(() => {
  const CLOSE_DELAY = 160;

  // Shared across every bound item (Products, Company, …): tracks which
  // item currently "owns" the header's solid-background state. Multiple
  // independent bind() closures each had their own close timer but all
  // toggled the SAME header.fc-mega-active class — a stale, merely-delayed
  // close from the item you just left could fire after you'd already
  // opened the next item, stripping the class the new panel depends on for
  // its background. Routing every open/close through this single owner
  // reference makes "is any mega item open" one source of truth: a close
  // only touches the shared class if it still owns it, and opening a new
  // item immediately hands off (closing whatever previously held it)
  // instead of racing a pending timer.
  const shared = { owner: null };

  function bind(item) {
    if (!item || item.dataset.fcMegaBound === 'true') return;
    const trigger = item.querySelector('[data-fc-mega-trigger]');
    if (!trigger) return;
    item.dataset.fcMegaBound = 'true';

    // Header element carries .fc-mega-active so the bar paints solid forest
    // while the panel is open (the transparent hero header otherwise shows
    // through behind the nav + CTA).
    const header = item.closest('header-component');

    let closeTimer;
    const isOpen = () => item.classList.contains('fc-mega-open');

    const open = () => {
      clearTimeout(closeTimer);
      if (isOpen()) {
        shared.owner = closeNow;
        return;
      }
      // Hand off immediately: a different item may still be open (or have a
      // close scheduled) — close it now rather than letting its delayed
      // timer fire later and strip the class this item is about to claim.
      if (shared.owner && shared.owner !== closeNow) shared.owner();
      positionDropPanel();
      item.classList.add('fc-mega-open');
      if (header) header.classList.add('fc-mega-active');
      trigger.setAttribute('aria-expanded', 'true');
      shared.owner = closeNow;
    };

    const closeNow = () => {
      clearTimeout(closeTimer);
      if (!isOpen()) return;
      item.classList.remove('fc-mega-open');
      trigger.setAttribute('aria-expanded', 'false');
      // Only relinquish the shared header state if this item still owns it
      // — guards against a stale call (e.g. a pending timer that fired
      // after another item already took over) wiping the active item's
      // background.
      if (shared.owner === closeNow) {
        shared.owner = null;
        if (header) header.classList.remove('fc-mega-active');
      }
    };
    const close = closeNow;

    const scheduleClose = () => {
      clearTimeout(closeTimer);
      closeTimer = setTimeout(close, CLOSE_DELAY);
    };

    const toggle = () => (isOpen() ? close() : open());
    const hoverCapable = () => window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    // A trigger with no real destination (e.g. COMPANY as a pure group
    // label with href="#") is a disclosure only — never navigate it.
    const href = trigger.getAttribute('href') || '';
    const navigable = href !== '' && href !== '#';

    // Compact dropdown (COMPANY et al.): the panel is absolutely positioned
    // against the same containing block as the Products panel (the header
    // row), so it drops from the header's bottom edge. Horizontal centering
    // under the trigger is measured here since that block spans the header.
    const dropPanel = item.querySelector('.menu-list__submenu--fc-drop');
    const positionDropPanel = () => {
      if (!dropPanel) return;
      const anchor = dropPanel.offsetParent;
      if (!anchor) return;
      const triggerRect = trigger.getBoundingClientRect();
      const anchorRect = anchor.getBoundingClientRect();
      const center = triggerRect.left + triggerRect.width / 2 - anchorRect.left;
      dropPanel.style.setProperty('--fc-drop-left', `${center}px`);
    };

    // Hover intent
    item.addEventListener('mouseenter', open);
    item.addEventListener('mouseleave', scheduleClose);

    // Click: navigate to the trigger's page on hover-capable pointers (the
    // panel already opened on hover); on touch, open the panel instead.
    // Non-navigable triggers (href="#") always toggle the disclosure.
    trigger.addEventListener('click', (event) => {
      if (hoverCapable() && navigable) return;
      event.preventDefault();
      toggle();
    });

    // Keyboard: Space always toggles the disclosure. Enter follows the link on
    // hover-capable devices (focus already revealed the panel) and toggles on
    // touch. Escape closes and restores focus.
    trigger.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && isOpen()) {
        event.preventDefault();
        close();
        trigger.focus();
      } else if (event.key === ' ' || event.key === 'Spacebar') {
        event.preventDefault();
        toggle();
      } else if (event.key === 'Enter' && (!hoverCapable() || !navigable)) {
        event.preventDefault();
        toggle();
      }
    });

    // Focus management
    item.addEventListener('focusin', open);
    item.addEventListener('focusout', (event) => {
      if (!item.contains(event.relatedTarget)) scheduleClose();
    });

    // Global dismissers
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && isOpen()) {
        close();
        trigger.focus();
      }
    });
    document.addEventListener('pointerdown', (event) => {
      if (isOpen() && !item.contains(event.target)) close();
    });
  }

  function init() {
    document.querySelectorAll('[data-fc-mega-item]').forEach(bind);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-bind after theme-editor section re-renders
  document.addEventListener('shopify:section:load', init);
})();
